/**
 * @graphql cc
 */
import { useEffect, useMemo } from 'react';
import { gql } from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isUnauthenticatedError } from '@confluence/error-boundary/entry-points/isUnauthenticatedError.ts';
import type { Template } from '@confluence/create-content-from-template/entry-points/createContentFromTemplateTypes.ts';
import {
	ONBOARDING_EXPERIENCES,
	EDITOR_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { extractContentIdFromUrl } from './NthUserOnboardingFlag.tsx';
import type {
	useNthUserOnboardingBannerGetTeamTypeQuery,
	useNthUserOnboardingBannerGetTeamTypeQueryVariables,
} from './__types__/useNthUserOnboardingBannerGetTeamTypeQuery.ts';
import type {
	useNthUserOnboardingBannerGetTemplateInfoQuery,
	useNthUserOnboardingBannerGetTemplateInfoQueryVariables,
} from './__types__/useNthUserOnboardingBannerGetTemplateInfoQuery.ts';
import { TEAM_TYPE_BLUEPRINT_MAPPING, Team } from './teamTypes.ts';

export function isExpectedError(error: Error) {
	return isUnauthorizedError(error) || isUnauthenticatedError(error);
}

export interface NthUserOnboardingBannerProps {
	contentID: string;
}

export interface NthUserOnboardingBannerData {
	isUserEligible: boolean;
	isLoading: boolean;
	blueprintName: string;
	teamType: Team;
	templateToCreate: Template | null;
}

export const useNthUserOnboardingBanner = ({
	contentID,
}: NthUserOnboardingBannerProps): NthUserOnboardingBannerData => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		ONBOARDING_EXPERIENCES.NTH_USER_BANNER_DISMISSED,
		ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_NTH_USER_PATH,
		EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
	]);

	const { hasUserPublished, nthUserGSPagePath, nthUserBannerDismissed } =
		deserializeState(onboardingStateData);

	const isCorrectContentId =
		nthUserGSPagePath && extractContentIdFromUrl(nthUserGSPagePath) === contentID;

	// Skip all side effects if user is not eligible
	const isUserEligible =
		!isOnboardingStateLoading &&
		!onboardingStateError &&
		!nthUserBannerDismissed &&
		!hasUserPublished &&
		nthUserGSPagePath &&
		isCorrectContentId;

	// Query to get the team type of the user from identity service
	const {
		data,
		loading: isTeamTypeLoading,
		error: teamTypeError,
	} = useQueryInterop<
		useNthUserOnboardingBannerGetTeamTypeQuery,
		useNthUserOnboardingBannerGetTeamTypeQueryVariables
	>(
		// @ts-expect-error -- http://go/cfe-use-relay-artifacts
		gql`
			query useNthUserOnboardingBannerGetTeamTypeQuery($userId: ID!) {
				user: confluence_atlassianUser(id: $userId) {
					team
				}
			}
		`,
		{
			variables: {
				userId: userId ?? '',
			},
			skip: !userId || !isUserEligible,
		},
	);

	const teamType = data?.user?.team;
	useEffect(() => {
		if (!isUserEligible) return;
		if (teamTypeError && isExpectedError(teamTypeError)) {
			markErrorAsHandled(teamTypeError);
		} else if (teamTypeError) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'nthUserGSPage',
					actionSubject: 'teamTypeFetching',
					action: 'failed',
					actionSubjectId: 'nthUserOnboardingBanner',
					attributes: {
						error: teamTypeError?.message || teamTypeError || 'Unknown error',
					},
				},
			}).fire();
		}
	}, [teamTypeError, createAnalyticsEvent, isUserEligible]);

	// Query to get the template info from the blueprint key
	const blueprintKey = TEAM_TYPE_BLUEPRINT_MAPPING[(teamType as Team) ?? Team.Default];
	const {
		data: templateData,
		loading: isTemplateLoading,
		error: templateError,
	} = useQueryInterop<
		useNthUserOnboardingBannerGetTemplateInfoQuery,
		useNthUserOnboardingBannerGetTemplateInfoQueryVariables
	>(
		// @ts-expect-error -- http://go/cfe-use-relay-artifacts
		gql`
			query useNthUserOnboardingBannerGetTemplateInfoQuery($blueprintKey: ID!) {
				templateInfo(id: $blueprintKey) {
					name
					templateType
					contentBlueprintId
					blueprintModuleCompleteKey
					itemModuleCompleteKey
				}
			}
		`,
		{
			variables: {
				blueprintKey,
			},
			skip: !blueprintKey || !isUserEligible || teamType === Team.SoftwareDevelopment,
		},
	);
	useEffect(() => {
		if (!isUserEligible) return;
		if (templateError && isExpectedError(templateError)) {
			markErrorAsHandled(templateError);
		} else if (templateError) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'nthUserGSPage',
					actionSubject: 'templateFetching',
					action: 'failed',
					actionSubjectId: 'nthUserOnboardingBanner',
					attributes: {
						error: templateError?.message || templateError || 'Unknown error',
					},
				},
			}).fire();
		}
	}, [templateError, createAnalyticsEvent, isUserEligible]);

	const { templateInfo } = templateData || {};
	const { name: templateName } = templateInfo || {};

	const blueprintName = useMemo(() => {
		if (!isUserEligible) return '';
		if (teamType === Team.SoftwareDevelopment) {
			return blueprintKey;
		}
		if (teamType === Team.Operations) {
			return 'standard operating procedure';
		}
		return (templateName ?? '').toLowerCase();
	}, [isUserEligible, teamType, templateName, blueprintKey]);

	const isLoading = isTeamTypeLoading || isTemplateLoading;

	return {
		isUserEligible,
		isLoading,
		blueprintName,
		teamType: teamType as Team,
		templateToCreate: templateInfo as Template | null,
	};
};
